import { CommonModule } from '@angular/common';
import type { HttpErrorResponse } from '@angular/common/http';
import { computed, type ElementRef, type SimpleChanges, type Type, untracked } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  signal,
  viewChild,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';

import { PushPipe } from '@ngrx/component';
import { Store } from '@ngrx/store';
import {
  asyncScheduler,
  combineLatest,
  distinctUntilChanged,
  filter,
  from,
  map,
  merge,
  observeOn,
  of,
  startWith,
  switchMap,
} from 'rxjs';

import { provideAnalyticsViewContext } from '@core/analytics-legacy';
import { AnalyticsViewContext } from '@core/analytics-types';
import {
  BulkActionsController,
  BulkActionsSelectionMessageComponent,
  BulkActionsToolbarComponent,
} from '@core/bulk-actions-legacy';
import type { RtComponentOutletConfig } from '@core/common-legacy';
import {
  componentOutletConfigFactory,
  debounceAsap,
  DetachedRouterOutlet,
  filterBoolean,
  RtComponentOutlet,
} from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import {
  ButtonComponent,
  ButtonDropdownIndicatorDirective,
  ConnectedOverlayDirective,
  DividerComponent,
  DropdownComponent,
  DropdownHeaderComponent,
  DropdownItemComponent,
  DropdownWidth,
  GroupComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  OverlayOriginDirective,
  ResizeObserverEntryDirective,
  RtTooltip,
} from '@core/user-interface-legacy';
import { asViewRef, deepEqual, injectUntilDestroyed } from '@core/utils-legacy';
import { ViewActions, ViewSelectors } from '@core/views-data';
import type {
  CreateViewPayload,
  DataSource,
  DataSourceService,
  LayoutType,
  View,
  ViewSnapshot,
  ViewsSearchComponent,
} from '@core/views-types';
import { dataSource$, dataSourceService$ } from '@core/views-util';
import type { Visibility } from '@core/visibility-types';
import { DefaultVisibility } from '@core/visibility-types';

import { ViewsAnalytics } from '../../analytics/views-analytics';
import { provideInputService } from '../../filters/advanced-filters/tokens';
import { InputMultiSelectService } from '../../filters/input-types/input-multiselect/input-multiselect.service';
import { InputRangeService } from '../../filters/input-types/input-range/input-range.service';
import { InputSelectService } from '../../filters/input-types/input-select/input-select.service';
import { InputTextService } from '../../filters/input-types/input-text/input-text.service';
import { OptionsService } from '../../filters/input-types/options.service';
import { QuickFiltersContainerComponent } from '../../filters/quick-filters/quick-filters-container/quick-filters-container.component';
import { FiltersDataService } from '../../filters/services/filters-data.service';
import { FiltersFormDataService } from '../../filters/services/filters-form-data.service';
import { FiltersFormStateService } from '../../filters/services/filters-form-state.service';
import { FiltersFormService } from '../../filters/services/filters-form.service';
import { currentViewProvider$, provideCurrentView, provideViewElementRef } from '../consts';
import { SampleDataInfoboxComponent } from '../ui/sample-data-infobox/sample-data-infobox.component';
import { ViewDataService } from '../view-data.service';
import { ViewFieldsService } from '../view-fields.service';
import { ViewFormService } from '../view-form.service';
import type { ViewSettingsOpenAt } from '../view-settings/view-settings.component';
import { ViewSettingsComponent } from '../view-settings/view-settings.component';
import { ViewsNotificationsService } from '../views-notifications.service';
import { DefaultSearchComponent } from './default-search/default-search.component';
import { ViewsEmptyStateComponent } from './empty-state/views-empty-state.component';
import { toolbarCollapse, titleAnimation, filtersAnimation } from './header-animation';
import { LayoutSharedService } from './layouts/layout-shared.service';
import { ViewsLocalStorageService } from './local-storage.service';
import { ViewPageUIService } from './view-page-ui.service';
import type { ToolbarState } from './view-page.service';
import { ViewPageService } from './view-page.service';
import { ViewSortComponent } from './view-sort/view-sort.component';

@Component({
  selector: 'rt-views-page',
  imports: [
    ButtonComponent,
    ButtonDropdownIndicatorDirective,
    CommonModule,
    ConnectedOverlayDirective,
    DividerComponent,
    DropdownComponent,
    DropdownHeaderComponent,
    DropdownItemComponent,
    GroupComponent,
    HeadingComponent,
    IconButtonComponent,
    IconComponent,
    OverlayOriginDirective,
    QuickFiltersContainerComponent,
    ReactiveFormsModule,
    RouterLink,
    RtTooltip,
    TranslatePipe,
    ViewSettingsComponent,
    ViewsEmptyStateComponent,
    PushPipe,
    RtComponentOutlet,
    BulkActionsSelectionMessageComponent,
    BulkActionsToolbarComponent,
    DetachedRouterOutlet,
    SampleDataInfoboxComponent,
    ResizeObserverEntryDirective,
    ViewSortComponent,
  ],
  templateUrl: './view-page.component.html',
  styleUrls: ['./view-page.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [toolbarCollapse, titleAnimation, filtersAnimation],
  providers: [
    ViewFormService,
    FiltersFormService,
    FiltersFormDataService,
    FiltersFormStateService,
    FiltersDataService,
    ViewPageService,
    ViewPageUIService,
    ViewDataService,
    ViewsNotificationsService,
    ViewFieldsService,
    OptionsService,
    LayoutSharedService,
    ViewsAnalytics,
    provideCurrentView(),
    provideInputService('select', InputSelectService),
    provideInputService('multi_select', InputMultiSelectService),
    provideInputService('text', InputTextService),
    provideInputService('range', InputRangeService),
    provideAnalyticsViewContext(AnalyticsViewContext.Views),
    provideViewElementRef({ useFactory: () => inject(ViewsPageComponent).viewElementRef }),
  ],
})
/* eslint-disable max-lines */
export class ViewsPageComponent {
  private readonly store = inject(Store);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly viewFormService = inject(ViewFormService);
  private readonly viewPageService = inject(ViewPageService);
  private readonly viewPageUIService = inject(ViewPageUIService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly viewsAnalytics = inject(ViewsAnalytics);
  private readonly viewDataService = inject(ViewDataService);
  private readonly i18n = inject(I18nService);

  public readonly viewElementRef = viewChild.required<ElementRef<HTMLElement>>('viewContainer');
  public readonly headerContainerElRef =
    viewChild.required<ElementRef<HTMLElement>>('headerContainer');

  public readonly DropdownWidth = DropdownWidth;
  public readonly defaultVisibility = DefaultVisibility;

  @Input({
    transform: (viewId: string) => {
      const asNumber = parseInt(viewId, 10);
      const asString = viewId?.length > 0 ? viewId : null;
      return asNumber || asString;
    },
  })
  public viewId: number | null;

  public layoutContainerComponent: Type<unknown> | null = null;
  public currentView$ = this.viewDataService.routerViewId$.pipe(
    switchMap(viewId => this.store.select(ViewSelectors.byId(viewId ?? 0))),
  );
  public currentViewMeta$ = this.viewDataService.routerViewId$.pipe(
    switchMap(viewId => this.store.select(ViewSelectors.meta(viewId ?? 0))),
  );
  public pageReady = toSignal(
    this.viewDataService.routerViewId$.pipe(
      switchMap(viewId =>
        viewId ? this.currentViewMeta$.pipe(map(meta => meta.isLoaded)) : of(true),
      ),
    ),
    { initialValue: false },
  );
  public currentViewClean = signal<View | null>(null);
  public currentViewSnapshot = signal<ViewSnapshot | null>(null);
  public settingsOpenAt: ViewSettingsOpenAt = 'mainTemplate';
  public headerWidth = signal<number>(0);
  public headerHeight = 0;
  public dataSource: DataSource;
  public viewForm = this.viewFormService.viewForm;
  public visibility: Visibility | null = null;
  public dataSourceService: DataSourceService;
  public isEmpty: boolean;
  public isLoaded: boolean;
  public isViewCreate: boolean;
  public bulkActionsSelectedItems: Array<{ id: number }> = [];
  public bulkActionsButtonMode = (<const>{
    list: null,
    table: 'batchSelect',
    board: 'toggle',
  }) satisfies Record<LayoutType, unknown>;
  public viewFormChanged: boolean = false;
  public filtersChanged = computed(() => {
    const cleanView = this.currentViewClean();
    if (!cleanView) return false;

    const _notifier = this.viewFormService.formValue();
    const currentState = this.viewFormService.getValue();
    return untracked(() => this.viewFormService.hasFiltersChanged(cleanView, currentState));
  });
  public filtersApplied = this.viewFormService.filtersApplied;
  public viewsRecordsTotalCount: number | null = null;
  public hasSampleData: boolean = false;
  public isSampleDataInProgress: boolean = false;
  public toolbarState = toSignal(this.viewPageUIService.toolbarState$, { initialValue: 'visible' });
  public toolbarAnimationHandler = this.viewPageUIService.getToolbarAnimationHandler();
  public searchComponent: RtComponentOutletConfig<ViewsSearchComponent> =
    this.getSearchComponentConfig(DefaultSearchComponent);
  public currentLayoutType: LayoutType | null = null;

  @ViewChild('layoutOutlet', { static: true, read: ViewContainerRef })
  public layoutOutlet: ViewContainerRef;

  @ViewChild('viewSettingsOverlay', { static: true, read: ConnectedOverlayDirective })
  public settingsOverlay: ConnectedOverlayDirective;

  public viewListCountersMap: Record<View['id'], number> = {};

  private untilDestroyed = injectUntilDestroyed();
  private dataSource$ = inject(dataSource$);
  private dataSourceService$ = inject(dataSourceService$);
  private currentViewProvider$ = inject(currentViewProvider$);
  private layoutContainers = {
    list: () =>
      import('./layouts/layout-list/layout-list.component').then(c => c.LayoutListComponent),
    table: () =>
      import('./layouts/layout-table/layout-table.component').then(c => c.LayoutTableComponent),
    board: () =>
      import('./layouts/layout-board/layout-board.component').then(c => c.LayoutBoardComponent),
  };
  private bulkActionsController = inject(BulkActionsController);
  private viewsLocalStorage = inject(ViewsLocalStorageService);
  private rawViewFormValue: CreateViewPayload | null = null;

  constructor() {
    this.dataSource$.pipe(this.untilDestroyed).subscribe(dataSource => {
      this.dataSource = dataSource;
    });
    this.setUIElementRef();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.viewId && this.viewId !== changes.viewId.previousValue) {
      if (this.viewId) {
        this.isEmpty = false;
        this.isLoaded = false;
        this.viewDataService.clearLayoutCount();
        this.store.dispatch(
          ViewActions.fetchOne({
            payload: { dataSourceId: this.dataSource.id, viewId: this.viewId },
          }),
        );
      }
      this.viewDataService.routerViewId$.next(this.viewId);
    }
  }

  public ngOnInit() {
    this.redirectOnLoadingError();
    this.watchDataSourceService();
    this.watchClearBulkSelection();
    this.watchViewFormValueChanges();
    this.watchCurrentFiltersRecordsCount();
    this.watchDataSource();
    this.watchCurrentRouteView();
    this.watchRouterEvents();
    this.watchBulkActionsSelectionChange();
    this.watchLayout();
    this.watchSampleData();
    this.watchCounters();
  }

  public onDeleteView() {
    this.viewsAnalytics.deleteClick();
    this.viewPageService.delete(this.viewId!, () => {
      this.settingsOverlay.open = false;
      this.viewsAnalytics.deleteConfirmed();
    });
  }

  public saveView() {
    this.viewPageService.save();
    this.viewsAnalytics.viewSave();
  }

  public resetView() {
    this.viewPageService.resetView();
    this.viewsAnalytics.resetChanges();
    this.viewFormChanged = false;
  }

  public onDuplicateView() {
    this.viewsAnalytics.duplicateClick();
    this.viewPageService.duplicate(() => {
      this.viewsAnalytics.duplicateComplete();
    });
  }

  public onSettingsBackdropClick() {
    this.settingsOverlay.open = false;
    this.settingsOpenAt = 'mainTemplate';
  }

  public onSaveViewDropdownClick(
    origin: OverlayOriginDirective,
    overlay: ConnectedOverlayDirective,
  ) {
    overlay.origin = origin;
    overlay.recreateOverlay();
    overlay.open = true;
  }

  public onGroupingClick() {
    this.settingsOpenAt = 'groupingSelectTmpl';
    this.viewsAnalytics.settingsGroupByOpen({ origin: 'page' });
    this.settingsOverlay.open = true;
  }

  public onPageCtaClick(): void {
    this.viewsAnalytics.ctaClick({
      dataSourceId: this.dataSource.id,
      codename: this.currentViewClean()?.codename,
    });
    this.dataSourceService.onPageCtaClick();
  }

  public onHeaderContainerResize(
    entry: { height: number },
    headerEl: HTMLElement,
    toolbarState: ToolbarState,
  ) {
    if (toolbarState === 'hidden' || headerEl.classList.contains('ng-animating')) return;

    requestAnimationFrame(() => {
      if (asViewRef(this.cdRef).destroyed) return;

      this.headerHeight = entry.height;
      this.cdRef.detectChanges();
    });
  }

  private hasViewFormChanged(): boolean {
    if (!this.rawViewFormValue) return false;

    return !deepEqual(this.viewFormService.getValue(), this.rawViewFormValue);
  }

  private watchSampleData() {
    combineLatest([
      this.dataSourceService.hasSampleData$,
      this.dataSourceService.isSampleDataInProgress$,
    ])
      .pipe(observeOn(asyncScheduler), this.untilDestroyed)
      .subscribe(([hasSampleData, sampleDataInProgress]) => {
        this.hasSampleData = hasSampleData;
        this.isSampleDataInProgress = sampleDataInProgress;
        this.cdRef.detectChanges();
      });
  }

  private setUIElementRef() {
    toObservable(this.viewElementRef)
      .pipe(this.untilDestroyed)
      .subscribe(elementRef => this.viewPageUIService.setViewElementRef(elementRef));
  }

  private watchLayout() {
    const { type: layoutType } = this.viewForm.controls.layout.controls;

    layoutType.valueChanges
      .pipe(
        startWith(layoutType.value),
        filterBoolean,
        distinctUntilChanged(),
        switchMap(layoutType =>
          combineLatest([of(layoutType), from(this.layoutContainers[layoutType]())]),
        ),
        this.untilDestroyed,
      )
      .subscribe(([layoutType, layoutContainer]) => {
        if (!layoutContainer) {
          console.error('Unknown layout type: ', layoutType);
        }
        this.viewDataService.clearRecords();
        this.viewPageUIService.toolbarState$.next('visible');
        this.currentLayoutType = layoutType;
        this.layoutContainerComponent = layoutContainer;
        this.cdRef.detectChanges();
      });
  }

  private redirectOnLoadingError() {
    this.currentViewMeta$.pipe(this.untilDestroyed).subscribe(meta => {
      if (!meta.error) return;

      const httpError: HttpErrorResponse = meta.error;
      if (httpError.status === 404) {
        this.viewsLocalStorage.latestView.clear();
        this.router.navigate(['..'], { relativeTo: this.route });
      }
    });
  }

  private watchClearBulkSelection() {
    merge(
      this.viewForm.controls.layout.controls.type.valueChanges,
      this.viewForm.controls.group.controls.configId.valueChanges,
      this.viewForm.controls.layout.controls.swimlane.valueChanges,
      this.viewDataService.routerViewId$,
    )
      .pipe(this.untilDestroyed)
      .subscribe(() => this.bulkActionsController.clearSelection());
  }

  private watchDataSourceService() {
    this.dataSourceService$.pipe(this.untilDestroyed).subscribe(dataSourceService => {
      this.dataSourceService = dataSourceService;
      this.dataSourceService.viewFormService?.set(this.viewFormService);
      this.searchComponent = this.getSearchComponentConfig(
        dataSourceService.searchComponent ?? DefaultSearchComponent,
      );
      this.cdRef.detectChanges();
    });
  }

  private watchViewFormValueChanges() {
    this.viewForm.valueChanges.pipe(debounceAsap(), this.untilDestroyed).subscribe(() => {
      this.viewFormChanged = this.hasViewFormChanged();
      this.cdRef.detectChanges();
    });
  }

  private watchCurrentFiltersRecordsCount() {
    this.viewDataService.currentFiltersRecordsCount$
      .pipe(
        filter(({ data }) => !!data),
        this.untilDestroyed,
      )
      .subscribe(({ data, meta }) => {
        this.isLoaded = meta.isLoaded;
        this.viewsRecordsTotalCount = data!.meta.totalCount ?? null;
        this.isEmpty = this.isLoaded && data!.meta.totalCount === 0;
        this.cdRef.detectChanges();
      });
  }

  private watchDataSource() {
    this.dataSource$.pipe(this.untilDestroyed).subscribe(dataSource => {
      this.dataSource = dataSource;
      this.viewFormService.clear();
      if (this.isViewCreate) {
        this.viewFormService.setDefaults(this.dataSource);
      }
      this.cdRef.detectChanges();
    });
  }

  private watchCurrentRouteView() {
    this.currentView$.pipe(this.untilDestroyed).subscribe(viewData => {
      if (!viewData) {
        this.currentViewClean.set(null);
        this.currentViewSnapshot.set(null);
        this.currentViewProvider$.next(null);
        return;
      }

      const { view, snapshot } = viewData;
      this.currentViewClean.set(view);
      this.currentViewSnapshot.set(snapshot);
      this.viewsLocalStorage.latestView.set(view.id);
      const mergedView = this.viewDataService.mergeView(view, snapshot, this.dataSource);
      this.currentViewProvider$.next(mergedView);
      this.viewFormService.restoreFromView(this.dataSource, mergedView);
      this.viewDataService.refreshCurrentFiltersAllRecordsCount();
      this.viewPageUIService.toolbarState$.next('visible');
      this.viewForm.markAsPristine();
      this.viewFormChanged = false;
      this.rawViewFormValue = this.viewFormService.viewToFormValue(viewData.view);
      this.cdRef.detectChanges();
    });

    this.currentView$
      .pipe(
        filterBoolean,
        map(({ view }) => view.id),
        distinctUntilChanged(),
        this.untilDestroyed,
      )
      .subscribe(() => this.viewsAnalytics.open());
  }

  private watchRouterEvents() {
    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        map(() => {
          const { routeConfig, firstChild } = this.route.snapshot;
          return routeConfig?.path === 'create' || firstChild?.routeConfig?.path === 'create';
        }),
        distinctUntilChanged(),
        this.untilDestroyed,
      )
      .subscribe(isViewCreate => {
        this.isViewCreate = isViewCreate;
        this.viewPageService.isViewCreate$.next(isViewCreate);

        if (isViewCreate) {
          this.viewFormService.setDefaults(this.dataSource);
        }

        this.cdRef.detectChanges();
      });
  }

  private watchBulkActionsSelectionChange() {
    this.bulkActionsController.selectedItems$.pipe(this.untilDestroyed).subscribe(items => {
      this.bulkActionsSelectedItems = items;
      this.bulkActionsController.recalculateActions$.next();
      this.cdRef.detectChanges();
    });
  }

  private watchCounters() {
    combineLatest([
      this.viewPageService.viewListCountersMap$,
      this.viewDataService.currentFiltersRecordsCount$,
    ])
      .pipe(this.untilDestroyed)
      .subscribe(([countersMap]) => {
        this.viewListCountersMap = countersMap;
        this.cdRef.detectChanges();
      });
  }

  private getSearchComponentConfig(
    component: ViewsSearchComponent,
  ): RtComponentOutletConfig<ViewsSearchComponent> {
    return componentOutletConfigFactory(component, {
      inputs: {
        searchControl: this.viewForm.controls.searchQuery,
      },
    });
  }
}
